import {
  CustomMappingAssociatedValueType,
  CustomMappingOption,
  CustomMappingType,
  ICustomMapping,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  BASIC_MEDICAL_HISTORY_FORM_SCHEMA,
  Brand,
  CustomFormConfiguration,
} from '@principle-theorem/principle-core';
import {
  asyncForEach,
  Firestore,
  IBlobFilenamePair,
  multiSwitchMap,
  snapshot,
  WithRef,
  XSLXImporterExporter,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { IBaseAllergy } from '../interfaces';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { getValidMedicalHistoryFormOptions } from './allergy-to-medical-history';
import { IMedicalHistoryOption } from './allergy-to-medical-history-to-xlsx';
import { MedicalConditionToMedicalHistoryToXSLX } from './medical-condition-to-medical-history-to-xlsx';
import { XSLXToMedicalConditionToMedicalHistory } from './xlsx-to-medical-condition-to-medical-history';

export const MEDICAL_CONDITION_CUSTOM_MAPPING_TYPE = 'medicalConditionMapping';

export const MEDICAL_CONDITION_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Medical Condition Mapping',
    description: `Used to map medical conditions from the source PMS to Principle medical history.`,
    type: MEDICAL_CONDITION_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
});

export abstract class BaseMedicalConditionToMedicalHistoryHandler<
  MedicalCondition extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<MedicalCondition>,
> extends BaseCustomMappingHandler<object, string> {
  abstract sourceEntity: SourceEntity;
  abstract translateFn: (record: MedicalCondition['data']) => IBaseAllergy;

  customMapping = MEDICAL_CONDITION_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const medicalCondition = this.translateFn(record);
        return {
          label: medicalCondition.description,
          value: medicalCondition.id.toString(),
        };
      });
    return sortBy(options, 'label');
  }

  async getSelectionListOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    const options$ = Brand.medicalHistoryFormConfig$(
      migration.configuration.brand
    ).pipe(
      switchMap((config) =>
        config ? CustomFormConfiguration.getContent$(config) : of(undefined)
      ),
      map((content) => {
        const schema =
          content?.jsonSchemaForm.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
        return [
          {
            value: '',
            description: 'Omit',
            hasAssociatedValue: false,
            associatedValueType: CustomMappingAssociatedValueType.String,
            associatedValueDescription: '',
          },
          ...getValidMedicalHistoryFormOptions(schema).map((option) => ({
            value: option.path,
            description: option.mapTitle ?? '',
            hasAssociatedValue: option.associatedValues ? true : false,
            associatedValueType: CustomMappingAssociatedValueType.String,
            associatedValueDescription: 'Select an option',
          })),
        ];
      })
    );

    return snapshot(options$);
  }

  getAssociatedValueOptions$(
    migration: IPracticeMigration,
    destinationValue: { value: string }
  ): Observable<{ name: string }[]> {
    return Brand.medicalHistoryFormConfig$(migration.configuration.brand).pipe(
      switchMap((config) =>
        config ? CustomFormConfiguration.getContent$(config) : of(undefined)
      ),
      map((content) => {
        const schema =
          content?.jsonSchemaForm.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
        const selectedOption = getValidMedicalHistoryFormOptions(schema).find(
          (option) => option.path === destinationValue.value
        );
        return (selectedOption?.associatedValues ?? []).map((name) => ({
          name,
        }));
      })
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, allergies, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, allergies, translators);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, allergies, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, allergies, translators);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToMedicalConditionToMedicalHistory()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const medicalHistoryOptions =
      await this._getMedicalHistoryOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const mapTo = item.mapTo;
      const id = item.sourceId;
      const label = item.sourceName;
      const value = sourceOptions.find((option) => option.value === id)?.value;

      if (!value || !mapTo) {
        return;
      }

      const matchingOption = medicalHistoryOptions.find(
        (option) => option.label === mapTo
      );
      if (!matchingOption) {
        // eslint-disable-next-line no-console
        console.error(
          `No matching medical history option found for "${mapTo}"`
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          associatedValue: matchingOption.associatedValue,
          destinationValue: matchingOption.destinationValue,
          sourceIdentifier: value,
          sourceLabel: label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    allergies: ICustomMappingSourceOption[];
    translators: MedicalConditionToMedicalHistoryToXSLX;
  }> {
    const fileName = this.getFileName();
    const allergies = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);

    const medicalHistoryOptions =
      await this._getMedicalHistoryOptions(migration);
    const translators = new MedicalConditionToMedicalHistoryToXSLX(
      medicalHistoryOptions,
      await this.getRecords(translationMap)
    );
    return { fileName, allergies, translators };
  }

  private async _getMedicalHistoryOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IMedicalHistoryOption[]> {
    return snapshot(
      from(this.getSelectionListOptions(migration)).pipe(
        multiSwitchMap((option) => {
          if (!option.hasAssociatedValue) {
            return of([
              {
                destinationValue: { value: option.value },
                label: option.description,
              },
            ]);
          }

          return this.getAssociatedValueOptions$(migration, option).pipe(
            map((associatedValues) => {
              return associatedValues.map((associatedValue) => ({
                destinationValue: { value: option.value },
                associatedValue: associatedValue.name,
                label: `${option.description} - ${associatedValue.name}`,
              }));
            })
          );
        }),
        map((options) => options.flat())
      )
    );
  }
}
